import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Grid,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Select,
  MenuItem,
  Skeleton,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { Search as SearchIcon, FilterList as FilterIcon } from "@mui/icons-material";
import PocketBase from "pocketbase";
import "@coreui/coreui/dist/css/coreui.min.css";
import Slider from "react-slick";

const pb = new PocketBase(process.env.REACT_APP_POCKETBASE_URL);

const YouTubeVideos = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [videos, setVideos] = useState([]);
  const [search, setSearch] = useState("");
  const [subject, setSubject] = useState("All");
  const [categories, setCategories] = useState([{ id: "All", name: "All" }]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [slides, setSlides] = useState([]);
  const [, setCurrentSlide] = useState(0);
  

  const extractVideoId = (url) => {
    const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    beforeChange: (current, next) => setCurrentSlide(next),
  };


  useEffect(() => {
    const fetchVideosAndSubjects = async () => {
      try {
        setLoading(true);
        const subjectsData = await pb.collection("Subjects").getFullList({ sort: "-created" });
        const formattedSubjects = [{ id: "All", name: "All" }, ...subjectsData]; // Include 'All' as default

        setCategories(formattedSubjects);

        
        const videoRecords = await pb.collection("Recordings").getFullList({ sort: "-created" });

        setVideos(videoRecords);
      } catch (error) {
        setError(error);
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchSlides = async () => {
      try {
        const records = await pb.collection("Recording_Slides").getFullList();
        setSlides(records);
      } catch (error) {
        console.error("Error fetching mobile slides:", error);
      }
    };


    fetchSlides();
    fetchVideosAndSubjects();
  }, []);

  

  const filteredVideos = videos.filter(
    (video) =>
      (subject === "All" || video.subject === subject) &&
      video.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: "auto", minHeight: "100vh", backgroundColor: "background.default" }}>
      <Typography
        variant="h3"
        sx={{
          mb: 4,
          textAlign: "center",
          fontWeight: 700,
          color: "text.primary",
          letterSpacing: "-0.05rem",
          "&:after": {
            content: '""',
            display: "block",
            width: "60px",
            height: "4px",
            backgroundColor: "primary.main",
            margin: "8px auto 0",
          },
        }}
      >
        Kuppi Recordings
      </Typography>

      <div className="news-carousel h-32 overflow-hidden">
        <Slider {...settings}>
          {slides.map((news) => (
            <div key={news.id} className="carousel-slide">
              <img src={`${process.env.REACT_APP_POCKETBASE_URL}/api/files/${news.collectionId}/${news.id}/${news.image}`} alt={news.title} className="slide-image" />
              <div className="slide-content">
                <h3 className="slide-title text-white">{news.title}</h3>
                <p className="slide-excerpt">{news.excerpt}</p>
                
              </div>
            </div>
          ))}
        </Slider>
    </div>


      <Box sx={{ mb: 4, display: "flex", gap: 2, flexDirection: isMobile ? "column" : "row" }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search recordings..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: "action.active", mr: 1 }} />,
            sx: {
              borderRadius: "12px",
              backgroundColor: "background.paper",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "primary.light" },
            },
          }}
        />

        <Select
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          sx={{
            minWidth: 150,
            borderRadius: "12px",
            backgroundColor: "background.paper",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
          }}
          IconComponent={FilterIcon}
        >
          {categories.map((cat) => (
            <MenuItem key={cat.id} value={cat.id} sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{cat.name}</Typography>
            </MenuItem>
          ))}
        </Select>
      </Box>

      {error && (
        <Box sx={{ textAlign: "center", py: 4, color: "error.main" }}>
          <Typography variant="h6">Error loading videos. Please try again later.</Typography>
        </Box>
      )}

          

      <Grid container spacing={3}>
        {loading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Skeleton variant="rounded" height={200} sx={{ borderRadius: "16px" }} />
              <Skeleton variant="text" sx={{ mt: 1, fontSize: "1.5rem" }} />
              <Skeleton variant="text" width="60%" />
            </Grid>
          ))
        ) : filteredVideos.length > 0 ? (
          filteredVideos.map((video) => {
            const videoId = extractVideoId(video.link);
            return (
              <Grid item xs={12} sm={6} md={4} key={video.id}>
                <Card
                  sx={{
                    borderRadius: "16px",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  {videoId && (
                    <CardMedia
                      component="iframe"
                      height="200"
                      src={`https://www.youtube.com/embed/${videoId}?rel=0`}
                      title={video.name}
                      sx={{
                        border: "none",
                        borderRadius: "16px 16px 0 0",
                        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)",
                      }}
                    />
                  )}
                  <CardContent sx={{ backgroundColor: "background.paper", borderRadius: "0 0 16px 16px" }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {video.name}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mt: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          backgroundColor: "action.selected",
                          px: 1,
                          py: 0.5,
                          borderRadius: "4px",
                          color: "primary.main",
                        }}
                      >
                        {categories.find((s) => s.id === video.subject)?.name || "Unknown"}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })
        ) : (
          <Box sx={{ width: "100%", textAlign: "center", py: 4, color: "text.secondary" }}>
            <Typography variant="h6">No videos found{search && ` for "${search}"`}</Typography>
          </Box>
        )}
      </Grid>
    </Box>
  );
};

export default YouTubeVideos;
